export interface PassField {
  label: string;
  value: string;
}

export interface WalletActionLink {
  id: string;
  label: string;
  url: string;
  actionType?: string;
  // Apple Featured Actions / Google's front CTA — the merchant's small "put
  // this on the front" pick (max 2). Rendered as cards below the pass in the
  // web preview, matching Apple's documented Featured Actions layout, in
  // addition to (not instead of) the full back-of-pass link list.
  featured?: boolean;
}

export interface ApplePosterField {
  key: string;
  label: string;
  value: string;
}

export interface ApplePosterPreview {
  headerFields: ApplePosterField[];
  primaryFields: ApplePosterField[];
  // Value already reads "<label> <value>" — the device draws no footer label.
  footerFields: ApplePosterField[];
  // message/altText are null when hide_sensitive_preview is on.
  barcode: { format: string; message: string | null; altText: string | null } | null;
  featuredActions: Array<{ identifier: string; type: string; url: string; label: string }>;
}

export interface PassData {
  keyword?: string;
  organisation_name?: string;
  pass_name?: string;
  logo_path?: string;
  banner_path?: string;
  background_colour?: string;
  primary_colour?: string;
  secondary_colour?: string;
  position_header?: PassField | null;
  position_one?: PassField | null;
  position_two?: PassField | null;
  position_three?: PassField | null;
  position_four?: PassField | null;
  position_aux_one?: PassField | null;
  position_aux_two?: PassField | null;
  position_aux_three?: PassField | null;
  position_aux_four?: PassField | null;
  pass_type?: string;
  use_barcode?: boolean;
  barcode_type?: string; // code128|qr|pdf417|aztec — absent = code128
  pass_code?: string;
  // Pass download security (2026-08-30) — UI-only masking, per-template
  // opt-in. Backend still returns the real pass_code; the preview just
  // doesn't render it. See components/wallet/PassCard.tsx.
  hide_sensitive_preview?: boolean;
  // iOS 27 Poster Generic (2026-09) — additive Apple pass style. When true,
  // the actual .pkpass carries BOTH posterGeneric and the classic generic
  // block (iOS 26 fallback); the web preview offers a switcher between the
  // two so a merchant can see both without needing two devices.
  poster_generic_enabled?: boolean;
  // Poster Generic's dedicated full-bleed background — NOT banner_path
  // (a short wide strip, wrong aspect ratio for a full pass-face image).
  // Absent → the real pass renders on a plain background colour instead of
  // a stretched image; the preview matches that rather than falling back
  // to banner_path, so what you see here is what the pass will actually show.
  poster_artwork_path?: string;
  // Poster Generic's dedicated logo — confirmed live on a real device that
  // reusing logo_path does NOT render on the poster face at all. Backend
  // falls back to logo_path when this isn't set (better than nothing); the
  // preview mirrors that same fallback, unlike the background image above.
  poster_primary_logo_path?: string;
  // The iOS 27 poster face exactly as the .pkpass carries it — built
  // server-side by passApple's own builders (walletHandler
  // buildApplePosterPreview). The preview draws ONLY from this, never from
  // its own field mapping, so it can't drift from the pass.
  apple_poster?: ApplePosterPreview;
  tags?: PassField[];
  googleJwt?: string;
  applePassUrl?: string;
  walletActions?: WalletActionLink[];
  // Landing-page metadata (walletHandler ≥ v45). Optional so the page keeps
  // working against an older backend — tiles hide when values are absent.
  created_at?: string;
  last_modified?: string;
  expiration_date?: string;
  clicks?: number;
  // Samsung Wallet gates on for this pass (walletHandler ≥ p11, 2026-08-26).
  // Optional — absent on older backends means "not available".
  samsung_available?: boolean;
}

export const PASS_TYPE_LABELS: Record<string, string> = {
  loyalty: 'Loyalty card',
  gift_card: 'Gift card',
  event_ticket: 'Event ticket',
  coupon: 'Coupon',
  generic: 'Pass',
};

export function passTypeLabel(passType?: string): string {
  if (!passType) return 'Loyalty card';
  return PASS_TYPE_LABELS[passType] || passType;
}

// storeCard pass types render only ONE front field row on a real iPhone —
// aux slots move to the back. Mirrored here so the preview matches devices.
export function isStoreCardType(passType?: string): boolean {
  return passType === 'gift_card' || passType === 'loyalty' || !passType;
}

// hide_sensitive_preview was scoped to the explicit barcode/pass-code
// display block, but a template can bind that same value onto an ordinary
// front-of-pass field or back tag (e.g. a "Code" field showing the barcode
// value for readability) — those rendered it unmasked. Mask ANY field whose
// value matches the sensitive pass_code, wherever it appears on the page.
export function maskIfSensitiveCode(field: PassField, passData: PassData): PassField {
  if (passData.hide_sensitive_preview && passData.pass_code && field.value === passData.pass_code) {
    return { ...field, value: '••••••••' };
  }
  return field;
}

// Which wallet face the page should draw. Decided once from the device so
// the card matches the wallet: a Poster template opens on the iOS 27 face on
// any iPhone or desktop, other templates on the classic face, Android on the
// Google card. The switcher offers the other faces.
export type WalletFace = 'poster' | 'classic' | 'google';
export type DevicePlatform = 'ios' | 'android' | 'desktop';

export function defaultFaceFor(platform: DevicePlatform, iosMajor: number | null, passData: PassData): WalletFace {
  if (platform === 'android') return 'google';
  const posterOn = !!passData.poster_generic_enabled;
  // Poster templates open on the iOS 27 face on every Apple-capable device
  // (2026-10-13 product call); iOS 26 stays one tap away in the switcher.
  // iosMajor is kept in the signature for callers but no longer gates this.
  void iosMajor;
  return posterOn ? 'poster' : 'classic';
}

// Google Wallet ignores custom text colours and derives black/white from the
// card background. Mirror it so the Google face reads like the real card.
export function autoContrastColor(rgbOrHex?: string): string {
  if (!rgbOrHex) return '#ffffff';
  let r = 0, g = 0, b = 0;
  const m = rgbOrHex.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
  if (m) { r = +m[1]; g = +m[2]; b = +m[3]; }
  else if (/^#[0-9a-f]{6}$/i.test(rgbOrHex)) {
    r = parseInt(rgbOrHex.slice(1, 3), 16); g = parseInt(rgbOrHex.slice(3, 5), 16); b = parseInt(rgbOrHex.slice(5, 7), 16);
  } else return '#ffffff';
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6 ? '#1f1f1f' : '#ffffff';
}

export function formatDate(iso?: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}
